import { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useIsMobile } from '~/context/common/DeviceContext';
import GlobalContext from '~/context/vdp/GlobalContext';
import {
  getIsSpinnerDisabled,
  getLastModified,
  getSpinnerType,
  getVehicleDetails
} from '~/selectors';
import {
  BackgroundHeroImage,
  Container,
  HeroImage
} from './StaticVehicle.styles';

function useIsFallback() {
  const { spinnerReady } = useContext(GlobalContext);
  const isSpinnerDisabled = useSelector(getIsSpinnerDisabled);

  return spinnerReady.hasError || isSpinnerDisabled;
}

function useHeroImageUrl() {
  const isFallback = useIsFallback();
  const isMobile = useIsMobile();
  const { stockNumber, vexVdpImageData: { heroes: { hero = '' } = {} } = {} } =
    useSelector(getVehicleDetails);
  const lastModified = useSelector(getLastModified);

  const blur = isFallback ? 0.5 : 3;
  const contrast = isFallback || isMobile ? 0 : -30;
  const width = isFallback && !isMobile ? 1920 : 640;

  const heroImageUrl = new URL(
    hero ||
      `${process.env.CVNA_APP_CDNBLOB_BASE_URL}/${stockNumber}/pre/original/${stockNumber}-edc-03.jpg`
  );
  heroImageUrl.searchParams.append('blur', blur.toString());
  heroImageUrl.searchParams.append('contrast', contrast.toString());
  heroImageUrl.searchParams.append('crop', '16:9');
  heroImageUrl.searchParams.append('width', width.toString());
  heroImageUrl.searchParams.append('v', lastModified);

  return heroImageUrl.href;
}

type OwnProps = {
  isVisible: boolean;
};

export default function StaticVehicle({ isVisible }: OwnProps) {
  const heroImageUrl = useHeroImageUrl();
  const isFallback = useIsFallback();
  const isMobile = useIsMobile();
  const spinnerType = useSelector(getSpinnerType);

  const [isHidden, setHidden] = useState(false);
  const [isRemoved, setRemoved] = useState(false);

  useEffect(() => {
    setHidden(!isVisible);
    const t = window.setTimeout(() => {
      setRemoved(!isVisible);
    }, 1000);

    return () => {
      clearTimeout(t);
    };
  }, [isVisible, setHidden]);

  return isRemoved ? null : (
    <Container className={isHidden ? 'opacity-0' : 'opacity-100'}>
      <HeroImage
        isFallback={isFallback}
        spinnerType={spinnerType}
        src={heroImageUrl}
      />
      {!isMobile && <BackgroundHeroImage src={heroImageUrl} />}
    </Container>
  );
}
